import { portalText } from "../../i18n/portal.js";
/**
 * The console's Documentation articles — the prototype's `DOCS` (design/WeTalk.dc.html), which it
 * draws INSIDE the app shell (`data-screen-label="Docs"`; fidelity wave 2026-09-23).
 *
 * Copied as the prototype writes them, with the binding product decisions applied: money is in
 * euros (A2 / ADR 0014) and the sample order is in EUR. AMENDED 2026-10-12 (plan-017 WAPP-B, owner
 * answer OQ-1): no article names a price — the prototype's "fifty cents a minute" and "thirty an hour"
 * became "your rates", and the Billing article draws the organization's own rates through
 * `useTalkRates` (`docs-screen.tsx`), never a figure written here. The developer reference with the real
 * API (paths, envelopes, error codes) is the public site's `/docs/`; these articles are the
 * in-console how-to the prototype shows.
 *
 * AMENDED 2026-09-25 (customer-portal review #70, #71). The articles describe what a customer can
 * actually do today, not what the prototype drew:
 *
 *   - a number is set up BY WeTalk: the owner asks for one on Numbers & SIP ("Request a number")
 *     and an operator connects it. There is no bring-your-own-number flow, no SIP URI on the
 *     Numbers page for a carrier number, no voice webhook and no browser endpoint, so the
 *     prototype's `sip.wetalk.io`, `hooks.wetalk.io` and `wss://rtc.wetalk.io` samples are gone;
 *   - web chat is "soon" (its widget is not built), matching the Messaging screen;
 *   - no email is sent from the platform yet (no mail sender exists), so "you get an email" is not
 *     promised, and there is no "Email support" link: `support@wetalk.io` does not receive mail
 *     until wetalk.io mail is set up (D5). The rail says where a request does reach WeTalk.
 *     AMENDED 2026-10-13 (owner, docs refresh): support@wetalk.io receives mail now, so the rail
 *     says "Write to support@wetalk.io" with a mailto link, and still names the two requests that
 *     reach the wetalk team directly. Web chat is open too (see the 2026-10-13 amendment below).
 *
 * AMENDED 2026-09-26 (customer-portal review #187). "Webhooks and events" said to verify with "your
 * signing secret from Settings", which Settings can never show, and that delivery agents post
 * orders "to the URL you set during setup", which setup does not ask for. What is true today:
 * `POST /v1/webhook-endpoints` is the only way to add a destination and stores no signing secret;
 * the worker never sends an unsigned delivery, so none is sent; only `agent.published` is ever
 * produced. The article now says that, and describes the signed envelope as what deliveries WILL
 * carry (`apps/worker/src/handler/webhook-deliver.ts`, `webhook-signature.ts`).
 *
 * AMENDED 2026-09-27 (customer-portal review #241). "Create your first agent" said to "fill in
 * where results should go", which reads as if setup sends results somewhere (a webhook); none is
 * delivered (#238) and the Content step asks for no destination. The step now says where results
 * appear: the agent's History tab for every conversation, its Orders tab for an ordering agent.
 *
 * AMENDED 2026-10-13 (owner: "the whole documentation is outdated"; audit of origin/main c7ec8cb1).
 * The articles now say what the live product does: web chat is open (Messaging, allowed sites, voice
 * in the widget — ADR 0027); the Test console and the widget carry browser voice (ADR 0023); there is
 * no human takeover, so the "Handover" block is gone; forwarding to a phone is off until its spike
 * passes (ADR 0047), so an unknown question is a message plus a Review-tab gap; each language has its
 * own voice (ADR 0042); auto-reload sends a payment link (ADR 0040); caps are the three cap behaviours
 * of Finance → Allowances and caps; rates are per second and per kind of conversation (ADR 0054); a
 * trunk may only receive calls (ADR 0046). New blocks: the menu preview (ADR 0049), drafts (ADR 0043),
 * hours and busy choices (ADR 0044), order payments (ADR 0024), extra time (ADR 0055), financial
 * documents (ADR 0036); and two new articles, "Outbound campaigns" and "People, security & language"
 * (ADR 0033, 0037, 0050, 0051, 0052). Still no price in any article (OQ-1), and no model or carrier.
 *
 * AMENDED 2026-11-02 (plan-034 HELP-MOB; design §45, §45.26 – §45.28; D-3, D-4, D-11 as the owner changed them). A
 * ninth article, "The wetalk app" (`app`): what the app shows (one workspace at a time, as the role shows it in the
 * portal; no recordings, no orders), its acts (pause and resume an agent — Owner, Admin, Developer, the route's
 * `rolesAdmitting(OWNER_OR_ADMIN, "voice_agent.write")`; pause, resume and end a campaign — `campaign.operate`: Owner,
 * Admin, Member, Campaign manager), ending a campaign behind the statement and the typed name, signing in through the
 * portal's own pages, signed out after 90 days without use and by a password change or reset or a sign-in rule the
 * sign-in no longer meets, "Where you are signed in" ("wetalk app · iPhone" / "· Android"), the six notification kinds
 * (at most 20 an hour per phone, each kind switched per workspace in the app, the phone's own settings), and what stays
 * in the portal. The app's own words are quoted from `apps/mobile/src/i18n/catalog/**` and the push words from
 * `apps/worker/src/handler/push/word.ts`. No price, model or carrier is named.
 */

export interface DocsBlock {
  readonly heading: string;
  readonly paragraph: readonly string[];
  readonly bullet?: readonly string[];
  readonly code?: string;
  readonly codeLabel?: string;
}

export interface DocsArticle {
  readonly kicker: string;
  readonly title: string;
  readonly lede: string;
  readonly block: readonly DocsBlock[];
}

export const DOCS_ARTICLE_ID = ["start", "agents", "numbers", "channels", "webhooks", "billing", "campaigns", "account", "app"] as const;
export type DocsArticleId = (typeof DOCS_ARTICLE_ID)[number];

export function isDocsArticleId(value: string): value is DocsArticleId {
  return (DOCS_ARTICLE_ID as readonly string[]).includes(value);
}

/** The docs sidebar, in the prototype's order and wording. */
export const DOCS_NAVIGATION: readonly (readonly [DocsArticleId, string])[] = [
  ["start", portalText("core", "dashboard.create_your_first_agent_e9f219")],
  ["agents", portalText("core", "docs.agents_documents_ddba1b")],
  ["numbers", portalText("core", "docs.numbers_sip_dad16c")],
  ["channels", portalText("core", "docs.messaging_channels_3722ae")],
  ["webhooks", portalText("core", "docs.webhooks_events_e3b431")],
  ["billing", portalText("core", "docs.billing_limits_be0ddb")],
  ["campaigns", portalText("core", "docs.outbound_campaigns_c5ab2f")],
  ["account", portalText("core", "docs.people_security_language_0e4517")],
  ["app", portalText("core", "docs.the_wetalk_app_0bd8b0")],
];
export function docsNavigation(): typeof DOCS_NAVIGATION { return [
  ["start", portalText("core", "dashboard.create_your_first_agent_e9f219")],
  ["agents", portalText("core", "docs.agents_documents_ddba1b")],
  ["numbers", portalText("core", "docs.numbers_sip_dad16c")],
  ["channels", portalText("core", "docs.messaging_channels_3722ae")],
  ["webhooks", portalText("core", "docs.webhooks_events_e3b431")],
  ["billing", portalText("core", "docs.billing_limits_be0ddb")],
  ["campaigns", portalText("core", "docs.outbound_campaigns_c5ab2f")],
  ["account", portalText("core", "docs.people_security_language_0e4517")],
  ["app", portalText("core", "docs.the_wetalk_app_0bd8b0")],
]; }


/**
 * The rail's help line. AMENDED 2026-10-13 (owner: "we have support@wetalk.io we can use"): it says
 * "Write to support@wetalk.io" — the address is a mailto link `docs-screen.tsx` draws between the two
 * halves below — and still names the two requests that reach the wetalk team directly, both recorded
 * and listed for operators.
 */
export const SUPPORT_EMAIL = "support@wetalk.io";
export interface SupportLine {
  readonly before: string;
  readonly after: string;
}
export function supportLine(): SupportLine {
  return {
    before: portalText("core", "docs.need_a_hand_support_by_email_opens_once_wetalk_s_mailbox_is_live_2cd741"),
    after: portalText("core", "docs.request_numbers_sip_or_a_concurrency_request_billing_reaches_the_d8e618"),
  };
}


export const DOCS_ARTICLE: Readonly<Record<DocsArticleId, DocsArticle>> = {
  get start() { return {
    kicker: portalText("core", "docs.getting_started_76e7a3"),
    title: portalText("core", "dashboard.create_your_first_agent_e9f219"),
    lede: portalText("core", "docs.from_an_empty_workspace_to_an_agent_answering_your_number_nothing_df99c2"),
    block: [
      {
        heading: portalText("core", "docs.before_you_start_2c445d"),
        paragraph: [
          portalText("core", "docs.you_need_one_document_it_depends_on_the_agent_a_menu_for_delivery_7bf7c5"),
        ],
      },
      {
        heading: portalText("core", "docs.the_five_steps_24abd8"),
        paragraph: [],
        bullet: [
          portalText("core", "docs.pick_an_agent_from_the_library_it_already_knows_its_job_ced4fa"),
          portalText("core", "docs.upload_your_document_and_fill_in_what_the_template_asks_for_resul_03241a"),
          portalText("core", "docs.choose_languages_a_voice_and_a_greeting_06fa9a"),
          portalText("core", "docs.pick_channels_and_how_many_conversations_to_handle_at_once_c30154"),
          portalText("core", "docs.review_and_build_we_notify_you_when_the_agent_is_ready_16eefd"),
        ],
      },
      {
        heading: portalText("core", "docs.while_it_builds_fa7344"),
        paragraph: [
          portalText("core", "docs.tailoring_runs_in_the_background_and_you_can_close_the_page_when_7c1e7c"),
        ],
      },
      {
        heading: portalText("core", "docs.going_live_1db4de"),
        paragraph: [
          portalText("core", "docs.when_wetalk_has_connected_your_number_it_appears_on_numbers_sip_a_e61baa"),
        ],
      },
    ],
  }; },
  get "agents"() { return {
    kicker: portalText("core", "dashboard.agents_64acf7"),
    title: portalText("core", "docs.how_agents_use_your_documents_84a435"),
    lede: portalText("core", "docs.agents_answer_from_what_you_gave_them_they_do_not_invent_prices_t_63ab50"),
    block: [
      {
        heading: portalText("core", "docs.what_we_read_21ebce"),
        paragraph: [
          portalText("core", "docs.pdfs_csvs_docx_files_and_photos_of_printed_material_for_a_menu_we_178d34"),
        ],
      },
      {
        heading: portalText("core", "docs.keeping_it_current_03e814"),
        paragraph: [
          portalText("core", "docs.upload_a_new_version_any_time_from_the_agent_page_the_agent_is_re_b4eacf"),
        ],
      },
      // AMENDED 2026-10-13: the menu preview (ADR 0049) and drafts from answers and reviews (ADR 0043).
      {
        heading: portalText("core", "docs.checking_your_menu_first_4df2a7"),
        paragraph: [
          portalText("core", "docs.when_you_attach_a_menu_during_setup_wetalk_reads_it_straight_away_17ffe5"),
        ],
      },
      {
        heading: portalText("core", "docs.drafts_from_your_answers_and_reviews_0c49fe"),
        paragraph: [
          portalText("core", "docs.answering_questions_on_the_agent_s_review_tab_or_accepting_fixes_b102ee"),
        ],
      },
      {
        heading: portalText("core", "docs.when_the_agent_does_not_know_4ccee4"),
        paragraph: [
          portalText("core", "docs.it_says_so_and_transfers_to_the_person_you_nominate_or_takes_a_me_23f336"),
        ],
      },
      {
        heading: portalText("core", "docs.languages_db07be"),
        paragraph: [
          portalText("core", "docs.tick_the_languages_you_serve_the_agent_greets_in_the_first_one_an_5d2055"),
        ],
      },
      // AMENDED 2026-10-13: hours, special dates and busy choices (ADR 0044); order payments (ADR 0024).
      {
        heading: portalText("core", "docs.hours_special_dates_and_busy_lines_b58376"),
        paragraph: [
          portalText("core", "docs.set_the_agent_s_time_zone_and_opening_hours_and_add_special_dates_277c6c"),
          // AMENDED 2026-10-14 (owner decision, ADR 0061 amendment): a customer support agent answers at every hour;
          // its opening hours decide only when a caller can be put through to a person.
          portalText("core", "docs.support_answers_at_any_hour"),
        ],
      },
      {
        heading: portalText("core", "docs.payment_for_orders_366393"),
        paragraph: [
          portalText("core", "docs.when_wetalk_has_switched_on_online_payment_for_your_organization_2dec4d"),
        ],
      },
    ],
  }; },
  get numbers() { return {
    kicker: portalText("core", "docs.telephony_491886"),
    title: portalText("core", "docs.getting_a_phone_number_a18797"),
    lede: portalText("core", "docs.for_now_wetalk_sets_up_the_number_for_you_you_ask_from_the_consol_09809f"),
    block: [
      {
        heading: portalText("core", "docs.ask_for_a_number_8c0085"),
        paragraph: [
          portalText("core", "docs.open_numbers_sip_and_choose_request_a_number_pick_the_agent_that_fcc104"),
        ],
      },
      {
        heading: portalText("core", "docs.a_number_you_already_have_d20b00"),
        paragraph: [
          portalText("core", "docs.if_you_want_callers_to_keep_dialling_a_number_you_already_own_say_46e7e1"),
        ],
      },
      // plan-008 ST-UN (2026-09-30, R-UI-10): connecting a trunk is self-serve; the old "mention it in
      // a number request" block is replaced. WeTalk's SIP host and address are never written here —
      // the article sends the reader to Numbers & SIP, which renders them from the API.
      {
        heading: portalText("core", "docs.your_own_sip_trunk_4116a6"),
        paragraph: [
          portalText("core", "docs.if_your_numbers_are_with_a_voip_provider_or_on_your_own_phone_sys_ff5270"),
          portalText("core", "docs.your_provider_s_portal_has_what_to_enter_usually_on_a_page_called_0fbaf9"),
        ],
        bullet: [
          portalText("core", "docs.host_and_port_the_provider_s_sip_server_like_sip_provider_com_and_d6da21"),
          portalText("core", "docs.transport_udp_tcp_or_tls_as_the_provider_says_7e7a98"),
          portalText("core", "docs.username_and_password_what_wetalk_signs_in_with_the_password_is_k_e6cd5b"),
          portalText("core", "docs.your_numbers_in_international_form_with_and_which_of_them_your_ag_a31b52"),
          portalText("core", "docs.how_the_provider_writes_numbers_with_without_with_00_or_national_f40760"),
          portalText("core", "docs.the_countries_the_trunk_may_ring_emergency_short_and_premium_rate_e82c80"),
        ],
      },
      {
        heading: portalText("core", "docs.letting_your_provider_reach_wetalk_07f547"),
        paragraph: [
          portalText("core", "docs.numbers_sip_shows_wetalk_s_sip_host_and_address_under_where_to_po_fbcce2"),
          portalText("core", "docs.to_receive_conversations_add_your_provider_s_signalling_addresses_0c2ca4"),
          portalText("core", "docs.if_your_provider_sends_calls_to_a_phone_system_that_signs_in_to_i_dbe415"),
          // AMENDED 2026-10-13 (ADR 0046): a trunk may only receive calls.
          portalText("core", "docs.a_trunk_that_only_receives_calls_needs_no_outgoing_details_leave_44a41c"),
        ],
      },
      {
        heading: portalText("core", "docs.hd_voice_and_encryption_bfc402"),
        paragraph: [
          portalText("core", "docs.hd_voice_depends_on_your_provider_and_on_the_other_end_conversati_729631"),
          portalText("core", "docs.choose_if_the_provider_offers_it_to_encrypt_audio_when_the_provid_fdf25a"),
        ],
      },
      {
        heading: portalText("core", "docs.who_bills_what_c0cc6f"),
        paragraph: [
          portalText("core", "docs.your_provider_bills_its_own_minutes_for_calls_through_your_trunk_32d569"),
        ],
      },
      {
        heading: portalText("core", "docs.calling_from_a_browser_09451b"),
        paragraph: [
          portalText("core", "docs.not_available_yet_callers_reach_your_agent_on_its_phone_number_589aaa"),
        ],
      },
      {
        heading: portalText("core", "docs.one_carrier_per_number_60c6b8"),
        paragraph: [
          portalText("core", "docs.each_number_lives_with_one_phone_carrier_moving_it_to_another_car_0e88c6"),
        ],
      },
    ],
  }; },
  get "channels"() { return {
    kicker: portalText("core", "docs.channels_18e03e"),
    title: portalText("core", "docs.messaging_channels_3722ae"),
    lede: portalText("core", "docs.voice_is_live_today_web_chat_whatsapp_and_viber_are_in_progress_a_378b23"),
    block: [
      {
        heading: portalText("core", "docs.what_each_channel_needs_450a34"),
        paragraph: [],
        bullet: [
          portalText("core", "docs.whatsapp_business_a_meta_business_account_and_an_approved_templat_425353"),
          portalText("core", "docs.viber_a_viber_business_service_account_and_its_authentication_tok_02a5a8"),
          portalText("core", "docs.web_chat_one_script_tag_on_your_site_once_the_chat_widget_ships_998dc1"),
        ],
      },
      {
        heading: portalText("core", "account_admin.billing_abaec4"),
        paragraph: [
          portalText("core", "docs.messaging_is_billed_on_the_same_per_minute_basis_as_calls_measure_1f1232"),
        ],
      },
      // AMENDED 2026-10-13: the "Handover" block is gone — no person can take a conversation over.
    ],
  }; },
  get webhooks() { return {
    kicker: portalText("core", "settings.developers_71348b"),
    title: portalText("core", "docs.webhooks_and_events_387710"),
    lede: portalText("core", "docs.webhooks_will_send_what_happens_in_your_workspace_to_your_own_sys_db8302"),
    block: [
      {
        heading: portalText("core", "docs.where_webhooks_are_today_5ae34f"),
        paragraph: [
          portalText("core", "docs.webhooks_are_not_delivered_yet_every_delivery_will_be_signed_so_y_db18ed"),
          portalText("core", "docs.a_destination_is_added_through_the_api_for_now_post_v1_webhook_en_d403fa"),
        ],
      },
      {
        heading: portalText("core", "docs.orders_and_survey_answers_c7e56d"),
        paragraph: [
          portalText("core", "docs.neither_needs_a_webhook_confirmed_orders_are_kept_on_the_agent_s_c39294"),
        ],
      },
      {
        heading: portalText("core", "docs.event_types_0b420f"),
        paragraph: [
          portalText("core", "docs.a_destination_subscribes_to_one_or_more_of_these_so_far_only_agen_a861a8"),
        ],
        bullet: [
          portalText("core", "docs.agent_published_a_new_version_of_an_agent_went_live_f5265d"),
          portalText("core", "docs.call_started_call_ended_a_phone_conversation_began_or_ended_2bbf07"),
          portalText("core", "docs.thread_started_thread_ended_a_web_chat_or_messaging_conversation_5de415"),
          portalText("core", "docs.transcript_ready_a_conversation_s_transcript_is_ready_ca61de"),
          portalText("core", "docs.order_confirmed_booking_created_outcome_events_54485b"),
          portalText("core", "docs.campaign_finished_an_outbound_campaign_finished_14a229"),
        ],
      },
      {
        heading: portalText("core", "docs.what_a_delivery_will_look_like_4afc8f"),
        paragraph: [
          portalText("core", "docs.each_delivery_will_be_a_post_of_json_carrying_the_event_s_id_kind_f3a00f"),
          portalText("core", "docs.each_will_carry_an_x_wetalk_signature_header_t_the_time_in_unix_s_2f269c"),
        ],
        code: 'expected = hmac_sha256(secret, t + "." + raw_body)\nvalid    = timing_safe_equal(expected, v1) and abs(now - t) <= 300',
        codeLabel: portalText("core", "docs.pseudocode_0f0840"),
      },
    ],
  }; },
  get billing() { return {
    kicker: portalText("core", "account_admin.billing_abaec4"),
    title: portalText("core", "docs.credit_minutes_and_limits_2cdef1"),
    lede: portalText("core", "docs.charged_at_your_rates_by_the_minute"),
    block: [
      {
        heading: portalText("core", "docs.how_minutes_are_counted_899b3a"),
        paragraph: [
          portalText("core", "docs.billing_starts_when_the_agent_answers_and_stops_when_the_call_end_18067e"),
        ],
      },
      // AMENDED 2026-10-13 (ADR 0054): rates by kind of conversation, pinned at the start; tests are free.
      {
        heading: portalText("core", "docs.kinds_of_conversation_and_tests_c353f2"),
        paragraph: [
          portalText("core", "docs.your_rates_can_differ_by_kind_of_conversation_calls_to_your_wetal_f45b4b"),
        ],
      },
      {
        heading: portalText("core", "docs.credit_and_auto_reload_880f75"),
        paragraph: [
          portalText("core", "docs.add_credit_in_the_console_auto_reload_tops_up_by_a_fixed_amount_w_22dba9"),
        ],
      },
      // AMENDED 2026-10-13 (ADR 0055): the extra-time switch.
      {
        heading: portalText("core", "docs.extra_time_418096"),
        paragraph: [
          portalText("core", "docs.if_your_plan_includes_minutes_the_extra_time_switch_on_billing_de_740711"),
        ],
      },
      {
        heading: portalText("core", "docs.budgets_c13c82"),
        paragraph: [
          portalText("core", "docs.set_a_monthly_budget_with_an_email_alert_and_if_you_want_a_hard_s_efe939"),
        ],
      },
      {
        heading: portalText("core", "docs.concurrency_2ec390"),
        paragraph: [
          portalText("core", "docs.your_tier_caps_how_many_conversations_run_at_once_across_all_agen_5819cf"),
        ],
      },
      // AMENDED 2026-10-13 (ADR 0036): invoices, receipts, credit notes and VAT.
      {
        heading: portalText("core", "docs.invoices_receipts_and_vat_79b11f"),
        paragraph: [
          portalText("core", "docs.once_issued_your_invoices_receipts_and_credit_notes_are_listed_un_c187e7"),
        ],
      },
    ],
  }; },
  // AMENDED 2026-10-13: campaigns, contact lists and do-not-call, which the console has run since plan P14.
  get campaigns() { return {
    kicker: portalText("core", "workspaces.campaigns_01a23a"),
    title: portalText("core", "docs.outbound_campaigns_c5ab2f"),
    lede: portalText("core", "docs.a_campaign_rings_a_list_of_people_for_you_inside_the_hours_you_se_98cce9"),
    block: [
      {
        heading: portalText("core", "docs.contact_lists_b2cf5d"),
        paragraph: [
          portalText("core", "docs.add_a_list_on_contact_lists_by_uploading_a_csv_file_or_pasting_ro_d9f36f"),
        ],
      },
      {
        heading: portalText("core", "docs.starting_a_campaign_6deb00"),
        paragraph: [
          portalText("core", "docs.on_the_agent_s_outbound_tab_choose_new_campaign_pick_the_list_the_45b285"),
        ],
      },
      {
        heading: portalText("core", "docs.do_not_call_4e934f"),
        paragraph: [
          portalText("core", "docs.nobody_on_your_do_not_call_list_is_ever_rung_and_no_campaign_sett_8194b1"),
        ],
      },
    ],
  }; },
  // AMENDED 2026-10-13: people and roles (ADR 0033, 0052), the workspace kind (ADR 0050), customer-managed
  // security and scoped API keys (ADR 0037), and the console's English/Greek display language (ADR 0051).
  get account() { return {
    kicker: portalText("core", "account_admin.organization_519255"),
    title: portalText("core", "docs.people_security_and_language_852000"),
    lede: portalText("core", "docs.who_can_do_what_how_people_sign_in_and_the_language_the_console_s_46030c"),
    block: [
      {
        heading: portalText("core", "account_admin.people_and_roles_df661e"),
        paragraph: [
          portalText("core", "docs.your_organization_has_its_own_roles_owner_admin_finance_and_viewe_0ab584"),
        ],
      },
      {
        heading: portalText("core", "docs.test_workspaces_03298e"),
        paragraph: [
          portalText("core", "docs.when_you_create_a_workspace_you_mark_it_as_standard_or_as_a_test_f48925"),
        ],
      },
      {
        heading: portalText("core", "docs.signing_in_safely_5a8021"),
        paragraph: [
          portalText("core", "docs.under_my_security_add_an_authenticator_app_as_your_second_step_an_721e8d"),
          portalText("core", "docs.under_organization_security_the_owner_or_admin_can_verify_your_co_e51a09"),
        ],
      },
      {
        heading: portalText("core", "workspaces.api_keys_94fcf3"),
        paragraph: [
          portalText("core", "docs.keys_are_made_in_settings_either_for_one_workspace_or_for_the_who_cd1b5d"),
        ],
      },
      {
        heading: portalText("core", "docs.console_language_8beb7d"),
        paragraph: [
          portalText("core", "docs.the_console_speaks_english_and_greek_choose_yours_from_the_langua_67e63e"),
        ],
      },
    ],
  }; },
  // plan-034 HELP-MOB (design §45, §45.26 – §45.28; D-3, D-4, D-11 as the owner changed them): the wetalk app.
  get app() { return {
    kicker: portalText("core", "docs.on_your_phone_eb3e67"),
    title: portalText("core", "docs.the_wetalk_app_0bd8b0"),
    lede: portalText("core", "docs.the_wetalk_app_for_iphone_and_android_shows_your_agents_conversat_63f271"),
    block: [
      {
        heading: portalText("core", "docs.what_the_app_shows_5cd467"),
        paragraph: [
          portalText("core", "docs.the_app_shows_one_workspace_at_a_time_and_exactly_what_your_role_8525da"),
          portalText("core", "docs.recordings_and_orders_are_not_in_the_app_every_agent_campaign_and_fdcb13"),
        ],
        bullet: [
          portalText("core", "docs.today_the_day_at_a_glance_live_calls_and_conversations_minutes_to_af70aa"),
          portalText("core", "docs.agents_every_agent_and_its_state_for_one_agent_its_lines_when_it_876073"),
          portalText("core", "docs.live_the_conversations_happening_now_refreshed_every_few_seconds_7af778"),
          portalText("core", "docs.history_past_conversations_and_what_was_said_in_them_filtered_by_a0fde4"),
          portalText("core", "docs.campaigns_each_campaign_s_progress_its_calling_window_the_people_4a0847"),
          portalText("core", "docs.more_your_notices_with_mark_all_read_notifications_the_app_lock_a_74f20b"),
        ],
      },
      {
        heading: portalText("core", "docs.what_you_can_do_from_the_app_dd945a"),
        paragraph: [
          portalText("core", "docs.the_app_is_for_keeping_an_eye_on_things_plus_a_few_quick_acts_eac_3a47cc"),
        ],
        bullet: [
          portalText("core", "docs.pause_an_agent_and_resume_it_for_the_workspace_s_owner_admin_and_a5f6b8"),
          portalText("core", "docs.an_agent_that_wetalk_paused_shows_paused_by_wetalk_and_nobody_can_ef7bbc"),
          portalText("core", "docs.pause_a_campaign_and_resume_it_for_the_roles_that_can_run_campaig_5ee4a5"),
          portalText("core", "docs.end_a_campaign_for_the_same_roles_as_described_below_1e262f"),
          portalText("core", "docs.mark_your_notices_as_read_7d2fe6"),
        ],
      },
      {
        heading: portalText("core", "docs.ending_a_campaign_c18343"),
        paragraph: [
          portalText("core", "docs.end_campaign_is_on_a_campaign_s_screen_below_pause_and_resume_wh_78a235"),
          portalText("core", "docs.you_are_asked_twice_first_the_app_says_what_ending_means_with_con_16053b"),
        ],
      },
      {
        heading: portalText("core", "docs.signing_in_3819f5"),
        paragraph: [
          portalText("core", "docs.tap_sign_in_the_app_opens_the_wetalk_portal_in_your_phone_s_brows_3c1221"),
          portalText("core", "docs.the_app_creates_no_accounts_and_accepts_no_invitations_your_organ_57c055"),
        ],
      },
      {
        heading: portalText("core", "docs.staying_signed_in_and_signing_out_4a81f5"),
        paragraph: [
          portalText("core", "docs.the_app_stays_signed_in_while_you_use_it_after_90_days_without_us_9bed6e"),
          portalText("core", "docs.you_are_also_signed_out_when_you_change_or_reset_your_password_an_6f05c1"),
          portalText("core", "docs.for_extra_safety_turn_on_more_app_lock_wetalk_then_asks_for_face_ce61ac"),
        ],
      },
      {
        heading: portalText("core", "docs.the_app_in_where_you_are_signed_in_7c6435"),
        paragraph: [
          portalText("core", "docs.in_the_portal_my_security_where_you_are_signed_in_lists_the_app_a_af8af8"),
        ],
      },
      {
        heading: portalText("core", "docs.notifications_753a22"),
        paragraph: [
          portalText("core", "docs.after_your_first_sign_in_the_app_asks_whether_you_want_notificati_dae649"),
          portalText("core", "docs.there_are_six_kinds_each_says_what_happened_and_names_one_agent_o_6e1125"),
        ],
        bullet: [
          portalText("core", "docs.campaign_finished_a_campaign_has_called_everyone_it_was_going_to_3f936e"),
          portalText("core", "docs.campaign_ended_someone_ended_a_campaign_in_the_app_or_in_the_por_5f3a59"),
          portalText("core", "docs.campaign_can_t_call_a_running_campaign_can_call_nobody_because_i_52fad0"),
          portalText("core", "docs.paused_by_wetalk_or_resumed_by_wetalk_wetalk_paused_one_of_your_568ac0"),
          portalText("core", "docs.callers_are_being_turned_away_callers_cannot_be_answered_because_2ac1c0"),
          portalText("core", "docs.new_message_a_caller_left_a_message_sent_to_the_roles_that_can_r_f037fc"),
        ],
      },
      {
        heading: portalText("core", "docs.where_notifications_go_907832"),
        paragraph: [
          portalText("core", "docs.you_receive_them_for_every_workspace_you_belong_to_about_what_you_2e43bb"),
          // plan-034 REVIEW-FIX M2 (owner, 2026-11-02: "Rules-respecting"): only organizations whose sign-in rules the
          // phone's app sign-in meets.
          portalText("core", "docs.a_phone_receives_notifications_only_from_organizations_whose_30601a"),
        ],
      },
      {
        heading: portalText("core", "docs.turning_notifications_off_23b345"),
        paragraph: [
          portalText("core", "docs.more_notifications_has_a_switch_for_each_kind_for_the_workspace_t_3cca21"),
          portalText("core", "docs.to_stop_every_notification_from_wetalk_or_to_allow_them_again_aft_2f7d87"),
        ],
      },
      {
        heading: portalText("core", "docs.what_stays_in_the_portal_92a1b5"),
        paragraph: [
          portalText("core", "docs.the_app_changes_nothing_else_a_notice_about_one_of_the_things_bel_065b76"),
        ],
        bullet: [
          portalText("core", "docs.creating_agents_and_changing_them_documents_settings_drafts_and_p_f432dc"),
          portalText("core", "docs.numbers_and_sip_trunks_035c05"),
          portalText("core", "docs.creating_campaigns_contact_lists_and_your_do_not_call_list_908bff"),
          portalText("core", "docs.billing_plans_talk_time_and_invoices_61436f"),
          portalText("core", "docs.people_roles_and_invitations_10584a"),
          portalText("core", "docs.your_organization_s_settings_its_security_and_sign_in_rules_and_a_5daa4f"),
          portalText("core", "docs.your_own_security_and_where_you_are_signed_in_ae095e"),
        ],
      },
      {
        heading: portalText("core", "docs.phones_and_language_859359"),
        paragraph: [
          portalText("core", "docs.the_app_runs_on_iphones_with_ios_17_4_or_later_and_on_android_pho_aa003a"),
        ],
      },
    ],
  }; },
};
